import { APIDetails } from './apis-details';

export const aiInterviewApi: APIDetails = {
  id: 'API_KEYS.aiInterviewApi',
  title: 'AI Interview API',
  subtitle:
    'AI-powered interview orchestration, evaluation, and feedback—end-to-end.',
  description: [
    'Recruiters and hiring teams often struggle to maintain consistency, fairness, and efficiency during interviews. Interview questions vary by interviewer, evaluations can be subjective, and reviewing recordings or notes takes significant time—especially when hiring at scale.',
    'The AI Interview API solves these challenges by automating the entire interview lifecycle using AI. From generating structured interview questions based on job requirements, to securely collecting interview recordings, transcribing candidate responses, and producing objective AI-driven feedback, everything is handled through simple, scalable APIs.',
    'With the Interview API, companies can standardize interviews, reduce bias, speed up hiring decisions, and ensure every candidate is evaluated fairly using data-driven insights.',
  ],
  overview: [
    'AI Interview API is a powerful RESTful service designed to modernize and automate the interview process for HR platforms and recruitment systems.',
    '',
    'It enables:',
    '• Interview setup and configuration',
    '• AI-generated structured interview questions',
    '• Secure upload of interview recordings and documents',
    '• Automated transcription and AI evaluation',
    '• Objective interview feedback and performance insights',
  ],
  features: [
    {
      title: 'Interview Status Tracking',
      description: 'Check whether interview setup is ready for a job',
    },
    {
      title: 'AI-Generated Interview Questions',
      description: 'AI-powered questions tailored to job requirements',
    },
    {
      title: 'Standardized Interview Sessions',
      description: 'Consistent evaluation across candidates',
    },
    {
      title: 'Secure File Uploads',
      description: 'Presigned URLs for audio and documents',
    },
    {
      title: 'AI Interview Evaluation',
      description: 'Objective feedback using LLM analysis',
    },
    {
      title: 'Privacy & Data Control',
      description: 'Full interview data deletion support',
    },
  ],
  workflow: [
    'Recruiter checks interview configuration for a job',
    'Create an interview session for a candidate',
    'AI generates structured interview questions',
    'Interviewer conducts the interview',
    'Audio/video files are uploaded securely',
    'AI analyzes candidate responses',
    'Recruiters receive structured answers and AI feedback',
  ],
  endpoints: [
    {
      category: 'Interview Management',
      items: [
        {
          category: 'Get Interview Status',
          method: 'GET',
          path: '/api/v1/interviews/{job_id}',
          description:
            'Retrieves interview setup status and configuration for a specific job posting',
        },
        {
          category: 'Create Interview Session',
          method: 'POST',
          path: '/api/v1/interviews/',
          description:
            'Creates a new interview session and generates AI-driven interview questions',
        },
        {
          category: 'Get Interview Questions',
          method: 'GET',
          path: '/api/v1/interviews/{interview_id}/questions',
          description: 'Retrieves structured interview questions for a session',
        },
        {
          category: 'Get File Upload URLs',
          method: 'GET',
          path: '/api/v1/interviews/{interview_id}/get_presigned_url',
          description:
            'Generates secure presigned URLs for uploading interview files',
        },
        {
          category: 'Complete File Upload',
          method: 'POST',
          path: '/api/v1/interviews/{interview_id}/upload_complete',
          description:
            'Confirms upload completion and triggers transcription & AI analysis',
        },
        {
          category: 'Get Candidate Answers',
          method: 'GET',
          path: '/api/v1/interviews/{interview_id}/candidate_answer',
          description: 'Retrieves candidate responses from the interview',
        },
        {
          category: 'Get Interview Feedback',
          method: 'GET',
          path: '/api/v1/interviews/{interview_id}/feedback',
          description:
            'Retrieves AI-generated interview evaluation and feedback',
        },
        {
          category: 'Delete Interview Session',
          method: 'DELETE',
          path: '/api/v1/interviews/{interview_id}/delete',
          description: 'Deletes interview session and all related data',
        },
      ],
    },
  ],
  apiDocumentation: [
    // Jobs Endpoints
    {
      title: 'Create Job',
      method: 'POST',
      path: '/api/v1/jobs',
      requestPath: 'POST /api/v1/jobs',
      description: 'Creates a new job and triggers AI question generation',
      requestExample: {
        title: 'Software Engineer',
        description: 'Develop and maintain software applications.',
        company: 'Tech Corp',
        location: 'remote',
        skills: ['python', 'fastapi'],
      },
      responseExample: {
        title: 'Software Engineer',
        description: 'Develop and maintain software applications.',
        company: 'Tech Corp',
        location: 'remote',
        skills: ['python', 'fastapi'],
        job_id: 3,
        processing_status: 'pending',
        created_at: '2025-12-03T17:46:53.352773',
        updated_at: '2025-12-03T17:46:53.352813',
      },
      statusCode: '200',
    },
    {
      title: 'Get Job Details',
      method: 'GET',
      path: '/api/v1/jobs/{job_id}',
      requestPath: 'GET /api/v1/jobs/{job_id}',
      description: 'Retrieves job details along with generated questions',
      responseExample: {
        job_id: 3,
        processing_status: 'completed',
        message: 'AI has successfully generated the interview questions.',
        title: 'Software Engineer',
        questions: [
          {
            question_id: 1,
            question: 'How do you define a basic GET endpoint in FastAPI?',
            answer:
              'You use the `@app.get("/path")` decorator above an asynchronous Python function that returns the data.',
          },
          {
            question_id: 2,
            question:
              'What is a primary benefit of using a microservices architecture?',
            answer:
              'Microservices allow for independent development, deployment, and scaling of individual services, improving agility and resilience.',
          },
        ],
        created_at: '2025-12-03T17:46:53.773956',
        updated_at: '2025-12-03T17:47:13.345257',
      },
      statusCode: '200',
    },

    // Interview Session Management
    {
      title: 'Create Interview Session',
      method: 'POST',
      path: '/api/v1/interviews',
      requestPath: 'POST /api/v1/interviews',
      description:
        'Creates a new interview session and returns a unique interview_id and candidate link',
      requestExample: {
        job_id: 3,
        candidate_name: 'Ariq',
        candidate_email: 'shusom@gmail.com',
      },
      responseExample: {
        interview_id: 'b7d0bb84-3c4d-45a7-b615-cb85f6553106',
        job_id: 3,
        candidate_name: 'Ariq',
        candidate_email: 'shusom@gmail.com',
        status: 'created',
        created_at: '2025-12-03T17:48:25.136928',
      },
      statusCode: '200',
    },
    {
      title: 'Get Interview Questions',
      method: 'GET',
      path: '/api/v1/interviews/{interview_id}/questions',
      requestPath: 'GET /api/v1/interviews/{interview_id}/questions',
      description: 'Fetch all AI-generated questions for a session',
      responseExample: {
        interview_id: 'b7d0bb84-3c4d-45a7-b615-cb85f6553106',
        questions: [
          {
            question_id: '1',
            question: 'How do you define a basic GET endpoint in FastAPI?',
            answer:
              'You use the `@app.get("/path")` decorator above an asynchronous Python function that returns the data.',
          },
          {
            question_id: '2',
            question:
              'What is a primary benefit of using a microservices architecture?',
            answer:
              'Microservices allow for independent development, deployment, and scaling of individual services, improving agility and resilience.',
          },
        ],
        status: 'ready',
      },
      statusCode: '200',
    },
    {
      title: 'Get Presigned URL for Upload',
      method: 'GET',
      path: '/api/v1/interviews/{interview_id}/get_presigned_url',
      requestPath: 'GET /api/v1/interviews/{interview_id}/get_presigned_url',
      description:
        'Generate temporary S3 URLs for uploading candidate audio/video',
      requestExample: {
        interview_id: 'b7d0bb84-3c4d-45a7-b615-cb85f6553106',
        filename_1: 'audio_1.mp3',
        filename_2: 'video_1.mp4',
      },
      responseExample: {
        urls: [
          { url: '<presigned-url-1>', file_name: '1' },
          { url: '<presigned-url-2>', file_name: '2' },
        ],
      },
      statusCode: '200',
    },
    {
      title: 'Upload Complete',
      method: 'POST',
      path: '/api/v1/interviews/{interview_id}/upload_complete',
      requestPath: 'POST /api/v1/interviews/{interview_id}/upload_complete',
      description:
        'Notify backend of uploaded files to trigger transcription & AI analysis',
      requestExample: [
        { file_name: '1', s3_url: '<url-1>' },
        { file_name: '2', s3_url: '<url-2>' },
      ],
      responseExample: {
        interview_id: 'b7d0bb84-3c4d-45a7-b615-cb85f6553106',
        status: 'processing',
        message: '2 files received. AI transcription & analysis started.',
      },
      statusCode: '200',
    },
    {
      title: 'Fetch Candidate Answers',
      method: 'GET',
      path: '/api/v1/interviews/{interview_id}/candidate_answer',
      requestPath: 'GET /api/v1/interviews/{interview_id}/candidate_answer',
      description: 'Retrieve transcribed candidate answers',
      responseExample: {
        interview_id: 'b7d0bb84-3c4d-45a7-b615-cb85f6553106',
        candidate_name: 'Ariq',
        answers: [
          {
            question_id: '7b2851e5-de02-4964-973b-04eb060107a8',
            question: '...',
            answer_text: '...',
          },
        ],
        status: 'completed',
      },
      statusCode: '200',
    },
    {
      title: 'Fetch Candidate Scores & AI Feedback',
      method: 'GET',
      path: '/api/v1/interviews/{interview_id}/feedback',
      requestPath: 'GET /api/v1/interviews/{interview_id}/feedback',
      description: 'Retrieve transcribed answers with AI evaluation and scores',
      responseExample: {
        interview_id: 'b7d0bb84-3c4d-45a7-b615-cb85f6553106',
        candidate_name: 'Ariq',
        job_id: 3,
        questions: [
          {
            question_id: '7b2851e5-de02-4964-973b-04eb060107a8',
            question:
              'How do you define a basic GET endpoint in FastAPI? || Answer: You use the `@app.get("/path")` decorator above an asynchronous Python function that returns the data.',
            candidate_answer:
              'The primary HTTP method used in RESTful API development are GET, POST, PUT, and DELETE, which directly map to the core CRUD operation.',
            score: 15.0,
          },
          {
            question_id: '49f4f6cd-9820-4705-a51f-1ea98202445c',
            question:
              'What is a primary benefit of using a microservices architecture? || Answer: Microservices allow for independent development, deployment, and scaling of individual services, improving agility and resilience.',
            candidate_answer:
              'A key benefit of microservice is independent deployability, allowing teams to build and deploy service autonomous, which speed up release cycle and isolate failure.',
            score: 80.0,
          },
        ],
        overall_feedback: {
          strengths: [
            'Correctly identifies common HTTP methods',
            'Understands CRUD-HTTP verb mapping',
            'Uses appropriate RESTful terminology',
          ],
          weaknesses: [
            'Does not answer the specific question',
            'Lacks FastAPI-specific implementation details',
            'Fails to describe endpoint definition',
            'Misinterpreted the scope of the question',
          ],
          recommendation: 'Reject',
          overall_score: 15.0,
        },
        status: 'completed',
      },
      statusCode: '200',
    },
  ],
  useCases: [
    {
      title: 'Standardized Hiring Interviews',
      description:
        'Ensure every candidate is interviewed using the same structured questions and evaluation criteria.',
    },
    {
      title: 'High-Volume Recruitment',
      description:
        'Conduct and evaluate interviews efficiently when hiring at scale without increasing HR workload.',
    },
    {
      title: 'Fair & Bias-Reduced Assessments',
      description:
        'Minimize subjective judgment by using consistent, data-driven interview feedback.',
    },
    {
      title: 'Remote & Distributed Hiring',
      description:
        'Enable seamless virtual interviews with automated analysis and centralized feedback.',
    },
    {
      title: 'Faster Hiring Decisions',
      description:
        'Reduce time spent reviewing recordings and notes with instant interview insights and summaries.',
    },
    {
      title: 'ATS & HR Platform Integration',
      description:
        'Enhance existing recruitment systems with automated interview creation and evaluation.',
    },
    {
      title: 'Recruitment Agencies',
      description:
        'Manage interviews across multiple clients and roles with consistent quality and reporting.',
    },
    {
      title: 'Early-Career & Campus Hiring',
      description:
        'Handle large numbers of interviews while maintaining fairness and evaluation quality.',
    },
    {
      title: 'Compliance & Audit Readiness',
      description:
        'Maintain structured interview records and feedback for transparency and future review.',
    },
  ],
  targetUsers: [
    'HR Tech Companies',
    'SaaS Product Teams',
    'Recruitment Agencies',
    'Enterprises with structured hiring processes',
    'Developers building hiring & interview tools',
  ],
};
